import { Colors, ConfirmDialog, Pagination, Spinner } from '@amelio/ui-library';
import { useAtomValue } from 'jotai';

import {
    GROWTH_GLASS_SURFACE,
    GROWTH_PLACEHOLDER,
    GROWTH_SECTION_PADDING,
    GROWTH_SECTION_TITLE,
} from './growthStyles';

import type { EmployeeNotesSeed } from '@/api/performance-profile';

import { userInstanceAtom } from '@/atoms/userDetails';
import { NoteCard } from '@/components/EmployeeNotes/NoteCard';
import { NoteComposer } from '@/components/EmployeeNotes/NoteComposer';
import { useEmployeeNoteList } from '@/components/EmployeeNotes/useEmployeeNoteList';
import { useAmelioTranslations } from '@/hooks';
import { JOURNEY_NOTES_PAGE_SIZE } from '@/types/career-growth';
import { CAREER_NOTE_CATEGORY } from '@/types/employee-note';

interface CareerJourneyNotesProps {
    employeeId: string;
    /** The first page the growth aggregate already carries, so the first paint costs no request. */
    initialNotes?: EmployeeNotesSeed;
    dataTestId?: string;
}

/**
 * "Notes du parcours": the manager's running commentary on the employee's career, paged by the
 * server. The section is self-contained -- it fetches its own notes, its own authors and writes
 * its own mutations -- so the tab only has to hand it an employee.
 *
 * The editor stands under the notes at all times: a section opened to write in asks for nothing
 * before it can be written in.
 */
export const CareerJourneyNotes = ({
    employeeId,
    initialNotes,
    dataTestId = 'career-journey-notes',
}: CareerJourneyNotesProps) => {
    const { t } = useAmelioTranslations();
    // The composer announces who is writing, and on this tab that is always the reader themselves.
    const connectedUser = useAtomValue(userInstanceAtom);
    const {
        notes,
        authorsById,
        isLoading,
        isError,
        isForbidden,
        page,
        setPage,
        totalCount,
        totalPages,
        pageRange,
        draftKey,
        startNewDraft,
        createNote,
        isCreating,
        updateNote,
        isSavingNote,
        noteIdToDelete,
        setNoteIdToDelete,
        confirmDelete,
        isDeleting,
    } = useEmployeeNoteList({
        employeeId,
        category: CAREER_NOTE_CATEGORY,
        pageSize: JOURNEY_NOTES_PAGE_SIZE,
        seed: initialNotes,
        writeErrors: {
            createError: t('performanceProfile.growth.notes.createError'),
            updateError: t('performanceProfile.growth.notes.updateError'),
            deleteError: t('performanceProfile.growth.notes.deleteError'),
        },
        announceWrites: true,
    });

    const editorPlaceholder = t('performanceProfile.growth.notes.placeholder');

    return (
        <section
            className={`${GROWTH_GLASS_SURFACE} ${GROWTH_SECTION_PADDING} flex flex-col gap-6`}
            data-testid={dataTestId}
        >
            <h3 className={GROWTH_SECTION_TITLE}>{t('performanceProfile.growth.notes.title')}</h3>

            {isLoading && (
                <div className={'flex justify-center py-6'}>
                    <Spinner
                        size={'md'}
                        color={Colors.PRIMARY}
                    />
                </div>
            )}

            {/*
                An unread list is not an empty one: say so, and offer no way to write. A refusal is
                not a failure either -- the notes are gated on their own permission, so a reader of
                the Growth tab may well be refused these while everything else on it answers.
            */}
            {isError && (
                <p className={GROWTH_PLACEHOLDER}>
                    {isForbidden
                        ? t('performanceProfile.growth.notes.forbidden')
                        : t('performanceProfile.growth.notes.loadError')}
                </p>
            )}

            {!isLoading && !isError && (
                <>
                    {notes.length === 0 ? (
                        <p className={GROWTH_PLACEHOLDER}>
                            {t('performanceProfile.growth.notes.empty')}
                        </p>
                    ) : (
                        <div className={'flex flex-col gap-4'}>
                            {notes.map((note) => (
                                <NoteCard
                                    key={note.id}
                                    note={note}
                                    header={'identity'}
                                    author={
                                        note.authorId ? authorsById.get(note.authorId) : undefined
                                    }
                                    editorPlaceholder={editorPlaceholder}
                                    onSave={(draft) => updateNote(note, draft)}
                                    onRequestDelete={() => setNoteIdToDelete(note.id)}
                                    isSaving={isSavingNote(note.id)}
                                    dataTestId={`${dataTestId}-note-${note.id}`}
                                />
                            ))}
                        </div>
                    )}

                    {totalCount > JOURNEY_NOTES_PAGE_SIZE && (
                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            pageSize={JOURNEY_NOTES_PAGE_SIZE}
                            onPageChange={setPage}
                            resultsSummaryText={t(
                                'performanceProfile.growth.notes.pageSummary',
                                pageRange,
                            )}
                            dataTestId={`${dataTestId}-pagination`}
                        />
                    )}
                </>
            )}

            {!isError && (
                <NoteComposer
                    key={draftKey}
                    category={CAREER_NOTE_CATEGORY}
                    placeholder={editorPlaceholder}
                    author={{
                        user: connectedUser,
                        youLabel: t('performanceProfile.employeeNotes.you'),
                    }}
                    onSave={createNote}
                    onCancel={startNewDraft}
                    isSaving={isCreating}
                    dataTestId={`${dataTestId}-composer`}
                />
            )}

            <ConfirmDialog
                isOpen={!!noteIdToDelete}
                title={t('performanceProfile.growth.notes.deleteTitle')}
                message={t('performanceProfile.growth.notes.deleteMessage')}
                confirmLabel={t('performanceProfile.employeeNotes.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={confirmDelete}
                onCancel={() => setNoteIdToDelete(null)}
                isLoading={isDeleting}
                isDestructive
                dataTestId={`${dataTestId}-delete-dialog`}
            />
        </section>
    );
};
